import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, Trash2, UserPlus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const SellerRepsTab = ({ reps, activeBooth, fetchData }) => {
  const { t } = useTranslation();
  const [showRepModal, setShowRepModal] = useState(false);
  const [repForm, setRepForm] = useState({ name: '', email: '', password: '' });

  const inviteRep = async (e) => {
    e.preventDefault();
    if (!activeBooth) { toast.error('No booth selected'); return; }
    if (!repForm.password || repForm.password.length < 6) { toast.error('Please set a password (min 6 characters)'); return; }
    try {
      await axios.post(`${API}/reps/invite`, { booth_id: activeBooth.id, email: repForm.email, name: repForm.name, password: repForm.password });
      toast.success(
        <div><p><strong>Rep added successfully!</strong></p><p className="text-sm mt-1">{repForm.name} ({repForm.email}) can now log in with the password you set.</p></div>,
        { duration: 8000 }
      );
      setShowRepModal(false);
      setRepForm({ name: '', email: '', password: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add rep'); }
  };

  const removeRep = async (repId) => {
    try {
      await axios.delete(`${API}/reps/${repId}`);
      toast.success('Rep removed');
      fetchData();
    } catch (error) { toast.error('Failed to remove rep'); }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('dashboard.representatives')}</CardTitle>
            <CardDescription>{t('dashboard.representatives_desc')}</CardDescription>
            {activeBooth && (
              <div className="mt-2">
                {activeBooth.tier === 'basic' ? (
                  <Badge className="bg-slate-100 text-slate-600"><AlertCircle className="h-3 w-3 mr-1" /> Basic tier: No reps allowed</Badge>
                ) : activeBooth.tier === 'premium' ? (
                  <Badge className={reps.length >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}><Users className="h-3 w-3 mr-1" /> {reps.length} / 3 reps used</Badge>
                ) : (
                  <Badge className="bg-purple-100 text-purple-700"><Users className="h-3 w-3 mr-1" /> {reps.length} reps (Unlimited)</Badge>
                )}
              </div>
            )}
          </div>
          {activeBooth?.tier === 'basic' ? (
            <Button disabled className="opacity-50 cursor-not-allowed" title={t('dashboard.upgrade_to_add_reps')}><UserPlus className="mr-2 h-4 w-4" /> {t('dashboard.upgrade_to_add_reps').split(' ').slice(0, 4).join(' ')}</Button>
          ) : activeBooth?.tier === 'premium' && reps.length >= 3 ? (
            <Button disabled className="opacity-50 cursor-not-allowed" title="Premium tier limit reached"><UserPlus className="mr-2 h-4 w-4" /> Limit Reached</Button>
          ) : (
            <Button onClick={() => setShowRepModal(true)}><UserPlus className="mr-2 h-4 w-4" /> {t('dashboard.add_rep')}</Button>
          )}
        </CardHeader>
        <CardContent>
          {reps.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              {activeBooth?.tier === 'basic' ? (
                <><p className="text-slate-600 mb-4">{t('dashboard.no_reps')}</p><p className="text-sm text-slate-500">{t('dashboard.upgrade_to_add_reps')}</p></>
              ) : (
                <><p className="text-slate-600 mb-4">{t('dashboard.no_reps')}</p><p className="text-sm text-slate-500">{t('dashboard.representatives_desc')}</p></>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>{t('dashboard.name')}</TableHead><TableHead>{t('dashboard.email')}</TableHead><TableHead>{t('dashboard.status')}</TableHead><TableHead>{t('dashboard.actions')}</TableHead></TableRow></TableHeader>
              <TableBody>
                {reps.map((rep) => (
                  <TableRow key={rep.id}>
                    <TableCell className="font-medium">{rep.name}</TableCell>
                    <TableCell>{rep.email}</TableCell>
                    <TableCell><Badge className={rep.role === 'rep' || rep.is_password_set ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{rep.role === 'rep' || rep.is_password_set ? 'Active' : 'Pending Setup'}</Badge></TableCell>
                    <TableCell><Button variant="destructive" size="sm" onClick={() => removeRep(rep.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invite Rep Modal */}
      <Dialog open={showRepModal} onOpenChange={setShowRepModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Representative</DialogTitle>
            <DialogDescription>Add a team member to help manage attendee interactions</DialogDescription>
          </DialogHeader>
          <form onSubmit={inviteRep} className="space-y-4">
            <div><Label>Name</Label><Input value={repForm.name} onChange={(e) => setRepForm({ ...repForm, name: e.target.value })} required placeholder="Rep's full name" /></div>
            <div><Label>Company Email</Label><Input type="email" value={repForm.email} onChange={(e) => setRepForm({ ...repForm, email: e.target.value })} required placeholder="rep@yourcompany.com" /></div>
            <div><Label>Set Password</Label><Input type="password" value={repForm.password} onChange={(e) => setRepForm({ ...repForm, password: e.target.value })} required minLength={6} placeholder="Min 6 characters" />
              <p className="text-xs text-slate-500 mt-1">You&apos;ll share this password with the rep so they can log in</p></div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm"><p className="text-blue-800"><strong>How it works:</strong> The rep will log in using the main Sign In page with their company email and the password you set here.</p></div>
            <Button type="submit" className="w-full">Add Representative</Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SellerRepsTab;
